/**
 * Deakin Life search result card — matches news-and-media-releases listing tiles.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import type { SearchResultItem } from '../../../types';

dayjs.extend(customParseFormat);

export const BASE_CLASS = 'search-result-card';

type Props = {
  result: SearchResultItem;
};

const FUNNELBACK_PUBLISHED_DATE = 'YYYY-MM-DD HH:mm:ss';

function parsePublishedDate(raw: string): dayjs.Dayjs | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const funnelback = dayjs(trimmed, FUNNELBACK_PUBLISHED_DATE, true);
  if (funnelback.isValid()) {
    return funnelback;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed : null;
}

function formatPublishedDate(raw: string | undefined): string {
  if (!raw?.trim()) {
    return '';
  }
  const parsed = parsePublishedDate(raw);
  return parsed ? parsed.format('D MMM YYYY') : raw.trim();
}

function publishedDateTimeAttr(raw: string | undefined): string | undefined {
  if (!raw?.trim()) {
    return undefined;
  }
  const parsed = parsePublishedDate(raw);
  return parsed ? parsed.format('YYYY-MM-DD') : raw.trim();
}

const DeakinSearchResultCard = ({ result }: Props) => {
  const { typeLabel, title, url, publishedDate, thumbnailUrl } = result;
  const formattedDate = formatPublishedDate(publishedDate);
  const dateTimeAttr = publishedDateTimeAttr(publishedDate);
  const readMoreLabel = title ? `Read more about ${title}` : 'Read more';

  return (
    <article className={BASE_CLASS}>
      <div className={`${BASE_CLASS}__image-container`}>
        {thumbnailUrl ? (
          <img
            className={`${BASE_CLASS}__image`}
            src={thumbnailUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <div
            className={`${BASE_CLASS}__image-placeholder`}
            aria-hidden
          />
        )}
      </div>
      <div className={`${BASE_CLASS}__content`}>
        <h3 className={`${BASE_CLASS}__title`}>
          <a
            className={`${BASE_CLASS}__title-link`}
            href={url}
            rel="noopener noreferrer"
          >
            {title}
          </a>
        </h3>
        {typeLabel ? (
          <span className={`${BASE_CLASS}__category`}>{typeLabel}</span>
        ) : null}
        {formattedDate ? (
          <div className={`${BASE_CLASS}__meta`}>
            <CalendarTodayOutlinedIcon
              className={`${BASE_CLASS}__meta-icon`}
              aria-hidden
            />
            <time dateTime={dateTimeAttr}>{formattedDate}</time>
          </div>
        ) : null}
        <a
          className="link link-charcoal"
          href={url}
          rel="noopener noreferrer"
          aria-label={readMoreLabel}
        >
          Read more
        </a>
      </div>
    </article>
  );
};

export default DeakinSearchResultCard;
